import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TabPanel from '../general/TabPanel';
import { Box } from 'components/index';
import { updateProjectErrors, updateHiddenFieldWarnings } from './project.api';
import ProjectInfo from './ProjectInfo';
import ProjectDetails from './ProjectDetails';
import ProjectAnalysis from '../project-analysis/ProjectAnalysis';
import { FileUploadErrorDTO, MissingFieldWarningDTO, ProjectData, ProjectLineItemsAndSummary, ProjectSummary } from './project.type';
import { toast } from 'react-toastify';
import ProjectErrors from './ProjectErrors';
import { usePrompt } from '../AppPrompt';

interface ProjectProps {
  projectId: string;
  project: ProjectData;
  isLoadingProjectSummary: boolean;
  isErrorProjectSummary: boolean;
  errorProjectSummary: any;
  updateProject: (updatedProject: Partial<ProjectData>, isEditMode: boolean, updateInitialProject?: boolean) => void;
  updateInitialProject: (updatedDraw: ProjectLineItemsAndSummary) => void;
  isProjectDirty: boolean;
  isProjectLoading: boolean;
  isSaving: boolean;
  saveProject: (payappDrawId?: string) => void;
  undoChanges: () => void;
  refetchProject: () => void;
  handleCancel: () => void;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  readonly?: boolean;
}

const ProjectRenderer: React.FC<ProjectProps> = ({
  projectId,
  project,
  isLoadingProjectSummary,
  isErrorProjectSummary,
  errorProjectSummary,
  isProjectDirty: isProjectDirty,
  updateProject,
  updateInitialProject,
  isProjectLoading: isProjectLoading,
  isSaving,
  saveProject,
  undoChanges,
  refetchProject,
  handleCancel,
  isEditMode,
  setIsEditMode,
  readonly,
}) => {
  // const { projectId } = useParams();
  const [sectionIndex, setSectionIndex] = useState(0);

  const queryClient = useQueryClient();
  const [editedProjectSummary, setEditedProjectSummary] = useState<Partial<ProjectSummary>>({});
  const [projectErrors, setProjectErrors] = useState<FileUploadErrorDTO[]>([]);
  const [missingFieldWarnings, setMissingFieldWarnings] = useState<MissingFieldWarningDTO[]>([]);
  const { getConfirmation } = usePrompt();

  React.useEffect(() => {
    if (project.projectSummary && typeof project.projectSummary.errors === 'string') {
      try {
        const parsedErrors = JSON.parse(project.projectSummary.errors);

        if (Array.isArray(parsedErrors)) {
          setProjectErrors(parsedErrors);
        } else {
          console.error('Parsed errors are not in the expected array format:', parsedErrors);
        }
      } catch (e) {
        console.error('Error parsing the errors JSON:', e);
      }
    } else if (project.projectSummary && Array.isArray(project.projectSummary.errors)) {
      setProjectErrors(project.projectSummary.errors);
    }
  }, [project.projectSummary]);

  React.useEffect(() => {
    setMissingFieldWarnings(project.projectSummary?.missingFieldWarnings ?? []);
  }, [project.projectSummary]);

  const handleSectionChange = (event: any, newValue: any) => {
    if (isEditMode || isProjectDirty) {
      toast.error('You may have unsaved changes. Please save before navigating to another section.');
      return;
    }
    setSectionIndex(newValue);
  };

  const handleEditChange = (field: string, value: any) => {
    const updateNestedField = (obj: any, keys: string[], newValue: any): any => {
      const key = keys.shift();
      if (!key) return newValue;
      return {
        ...obj,
        [key]: updateNestedField(obj[key] || {}, keys, newValue),
      };
    };

    // Update the field in the ProjectSummary object
    setEditedProjectSummary(prevEditedProjectSummary => {
      const updatedProjectSummary = {
        ...updateNestedField(prevEditedProjectSummary, field.split('.'), value),
        isEdited: true,
      };
      updateProject({ projectSummary: updatedProjectSummary }, true);
      return updatedProjectSummary;
    });
  };

  React.useEffect(() => {
    if (project.projectSummary) {
      setEditedProjectSummary(project.projectSummary);
    }
  }, [project.projectSummary]);

  const sections = [
    {
      id: 'project-details',
      name: 'Project Details',
      component: (
        <ProjectDetails
          projectId={projectId}
          project={project}
          projectSummary={project.projectSummary}
          isLoadingProjectSummary={isLoadingProjectSummary}
          isErrorProjectSummary={isErrorProjectSummary}
          errorProjectSummary={errorProjectSummary}
          isSaving={isSaving}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          onEditChange={handleEditChange}
          onSave={saveProject}
          onCancel={handleCancel}
          projectErrors={projectErrors}
          isProjectDirty={isProjectDirty}
          updateProject={updateProject}
          updateInitialProject={updateInitialProject}
          readonly={readonly}
        />
      ),
    },
  ];
  if (!readonly) {
    sections.push({
      id: 'project-analysis',
      name: 'Project Analysis',
      component: <ProjectAnalysis projectErrors={projectErrors} />,
    });
  }

  const handleRemoveError = async (errorToRemove: string) => {
    const updatedErrors = projectErrors.filter(error => error.message !== errorToRemove);
    try {
      if (projectId) {
        const updateResponse = await updateProjectErrors(projectId, updatedErrors);
        setProjectErrors(updatedErrors);
        toast.success('Warning discarded successfully!');
      }
    } catch (error) {
      toast.error('Error discarding warning');
    }
  };

  // Warnings this session has hidden, kept alongside the currently-missing list so a hide call
  // can persist the full set rather than clobbering previously-hidden fields on the server (the
  // endpoint overwrites the whole HiddenFieldWarnings column, same as updateProjectErrors).
  const [hiddenFieldWarnings, setHiddenFieldWarnings] = useState<MissingFieldWarningDTO[]>([]);

  const handleHideWarning = async (fieldKey: string) => {
    const warningToHide = missingFieldWarnings.find(warning => warning.fieldKey === fieldKey);
    if (!warningToHide) return;
    const remainingWarnings = missingFieldWarnings.filter(warning => warning.fieldKey !== fieldKey);
    const updatedHidden = [...hiddenFieldWarnings, warningToHide];
    try {
      if (projectId) {
        await updateHiddenFieldWarnings(projectId, updatedHidden);
        setHiddenFieldWarnings(updatedHidden);
        setMissingFieldWarnings(remainingWarnings);
        toast.success('Warning hidden successfully!');
      }
    } catch (error) {
      toast.error('Error hiding warning');
    }
  };

  const handleDiscardAllErrors = async () => {
    try {
      if (projectId) {
        const isConfirmed = await getConfirmation({
          description: 'Are you sure you want to discard all warnings?',
          confirmButton: {
            label: 'Continue',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
        if (!isConfirmed) return;

        const updatedHidden = [...hiddenFieldWarnings, ...missingFieldWarnings];
        await Promise.all([updateProjectErrors(projectId, []), updateHiddenFieldWarnings(projectId, updatedHidden)]);
        setProjectErrors([]);
        setHiddenFieldWarnings(updatedHidden);
        setMissingFieldWarnings([]);
        toast.success('All warnings discarded successfully!');
      }
    } catch (error) {
      toast.error('Error discarding warnings');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: 0 }}>
      {!readonly && (projectErrors.length > 0 || missingFieldWarnings.length > 0) ? (
        <ProjectErrors
          errors={projectErrors}
          onRemoveError={handleRemoveError}
          onDiscardAllErrors={handleDiscardAllErrors}
          missingFieldWarnings={missingFieldWarnings}
          onHideMissingFieldWarning={handleHideWarning}
        />
      ) : null}
      <ProjectInfo
        project={project.projectSummary}
        isLoading={isLoadingProjectSummary}
        isError={isErrorProjectSummary}
        error={errorProjectSummary}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        onEditChange={handleEditChange}
        editsAllowed={sectionIndex == 0}
        projectErrors={projectErrors}
        readonly={readonly}
      />
      <Tabs
        value={sectionIndex}
        onChange={handleSectionChange}
        sx={{
          '.MuiTab-root': {
            '&:hover': {
              backgroundColor: '#eaf5f7',
            },
            fontWeight: 'bold',
          },
        }}
      >
        {sections.map((section, index) => (
          <Tab key={section.id} label={section.name} />
        ))}
      </Tabs>
      {sections.map((section, index) => (
        <TabPanel key={section.id} value={sectionIndex} index={index}>
          {sectionIndex === index && <>{section.component}</>}
        </TabPanel>
      ))}
    </Box>
  );
};

export default ProjectRenderer;
